import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LeadsLayout } from '@/components/LeadsLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

function LeadSourceMasters({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = React.useState('lead_source');
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [newItem, setNewItem] = React.useState({ name: '', description: '' });
  const [editingId, setEditingId] = React.useState(null);
  const [editData, setEditData] = React.useState({ name: '', description: '' });

  React.useEffect(() => {
    fetchItems();
    setNewItem({ name: '', description: '' });
    setEditingId(null);
  }, [activeTab]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/masters?type=${activeTab}`, getAuthHeader());
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching items:', error);
      toast.error('Failed to load items');
    }
    setLoading(false);
  };

  const handleAdd = async () => {
    if (!newItem.name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/masters`, {
        type: activeTab,
        name: newItem.name.trim(),
        description: newItem.description.trim()
      }, getAuthHeader());
      setNewItem({ name: '', description: '' });
      fetchItems();
      toast.success(`${activeTab === 'lead_source' ? 'Lead Source' : 'Sub Source'} added successfully`);
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error('Failed to add item');
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditData({ name: item.name, description: item.description || '' });
  };

  const handleSaveEdit = async () => {
    if (!editData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      await axios.put(`${BACKEND_URL}/api/masters/${editingId}`, {
        type: activeTab,
        name: editData.name.trim(),
        description: editData.description.trim()
      }, getAuthHeader());
      setEditingId(null);
      setEditData({ name: '', description: '' });
      fetchItems();
      toast.success('Updated successfully');
    } catch (error) {
      console.error('Error updating item:', error);
      toast.error('Failed to update');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData({ name: '', description: '' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/masters/${id}`, getAuthHeader());
      fetchItems();
      toast.success('Deleted successfully');
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete');
    }
  };

  return (
    <LeadsLayout user={user} onLogout={onLogout} title="Lead Source Masters">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800" data-testid="page-title">Lead Source Masters</h2>
            <p className="text-sm text-gray-500">Manage lead sources and sub-sources for the Lead Entry form</p>
          </div>
          <button
            onClick={() => navigate('/leads')}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            ← Back to Dashboard
          </button>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('lead_source')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === 'lead_source'
                  ? 'bg-emerald-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="lead-source-tab"
            >
              📊 Lead Sources
            </button>
            <button
              onClick={() => setActiveTab('sub_source')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === 'sub_source'
                  ? 'bg-emerald-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="sub-source-tab"
            >
              🏷️ Sub Sources
            </button>
          </div>
        </div>

        {/* Add New Item Form */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-bold text-gray-800 mb-4">
            Add New {activeTab === 'lead_source' ? 'Lead Source' : 'Sub Source'}
          </h3>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                value={newItem.name}
                onChange={(e) => setNewItem(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder={activeTab === 'lead_source' ? 'e.g., Social Media, Print Ad...' : 'e.g., Facebook, Instagram...'}
                data-testid="new-item-name"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={newItem.description}
                onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="Optional description..."
                data-testid="new-item-description"
              />
            </div>
            <button
              onClick={handleAdd}
              className="px-6 py-2 text-white rounded-xl font-medium shadow-[0_4px_12px_rgba(5,150,105,0.30)] hover:shadow-[0_8px_20px_rgba(5,150,105,0.40)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95"
              style={{ background: 'linear-gradient(135deg, #059669 0%, #0D9488 100%)' }}
              data-testid="add-item-btn"
            >
              + Add
            </button>
          </div>
        </div>

        {/* Items List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-800">
                {activeTab === 'lead_source' ? 'Lead Sources' : 'Sub Sources'} List
              </h3>
              <span className="text-sm text-gray-500">{items.length} items</span>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-5xl mb-4">📭</div>
              <p className="text-gray-500 mb-2">No {activeTab === 'lead_source' ? 'lead sources' : 'sub sources'} found</p>
              <p className="text-sm text-gray-400">Add your first item using the form above</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {items.map((item, index) => (
                <div
                  key={item.id}
                  className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  data-testid={`item-row-${item.id}`}
                >
                  {editingId === item.id ? (
                    /* Edit Mode */
                    <div className="flex-1 flex items-center gap-4">
                      <span className="text-gray-400 font-mono text-sm w-8">{index + 1}.</span>
                      <input
                        type="text"
                        value={editData.name}
                        onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        data-testid="edit-name-input"
                      />
                      <input
                        type="text"
                        value={editData.description}
                        onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        placeholder="Description..."
                        data-testid="edit-description-input"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveEdit}
                          className="p-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200"
                          title="Save"
                          data-testid="save-edit-btn"
                        >
                          ✓
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                          title="Cancel"
                          data-testid="cancel-edit-btn"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* View Mode */
                    <>
                      <div className="flex items-center gap-4">
                        <span className="text-gray-400 font-mono text-sm w-8">{index + 1}.</span>
                        <div>
                          <p className="font-medium text-gray-800">{item.name}</p>
                          {item.description && (
                            <p className="text-sm text-gray-500">{item.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(item)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                          data-testid={`edit-btn-${item.id}`}
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                          data-testid={`delete-btn-${item.id}`}
                        >
                          🗑️
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Card */}
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
          <div className="flex items-start gap-3">
            <span className="text-2xl">💡</span>
            <div>
              <h4 className="font-medium text-blue-800 mb-1">How it works</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• <strong>Lead Sources</strong> appear in the "Lead Source" dropdown on the Add Lead form</li>
                <li>• <strong>Sub Sources</strong> appear in the "Sub Source" dropdown for more detailed tracking</li>
                <li>• These values are used for reporting and analytics</li>
                <li>• If no custom sources are defined, default values will be used</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </LeadsLayout>
  );
}

export default LeadSourceMasters;
